<template>
  <div>
    <div ref="chartWrapperRef" v-click-outside="onChartClickOutside" class="tw:relative" @click="onChartAreaClick">
      <div :id="containerName" class="opportunity-boost-opportunities-chart tw:h-[340px] tw:md:h-[380px]"></div>
      <OpportunityBoostChartTooltip
        v-if="activeTooltip"
        :item="activeTooltip.item"
        :card="activeTooltip.card"
        :position="activeTooltip.position"
        @mouseenter="keepTooltipOpen"
        @mouseleave="scheduleHideTooltip" />
    </div>
    <div class="tw:flex tw:flex-wrap tw:justify-center tw:items-center tw:gap-x-6 tw:gap-y-2 tw:text-xs tw:mt-4">
      <div class="tw:flex tw:items-center tw:gap-2">
        <span class="tw:w-3 tw:h-3 tw:rounded-sm tw:bg-blue-500" />
        <span>{{ $t('rc_search_demand') }}</span>
      </div>
      <div class="tw:flex tw:items-center tw:gap-2">
        <span class="tw:w-3 tw:h-3 tw:rounded-sm tw:bg-blue-700" />
        <span>{{ $t('rc_demand_opportunity_graph') }}</span>
      </div>
      <div class="tw:flex tw:items-center tw:gap-2">
        <img :src="starIcon" alt="" class="tw:w-3 tw:h-3" />
        <span>{{ $t('rc_top_opportunity_graph') }}</span>
      </div>
      <div class="tw:flex tw:items-center tw:gap-2">
        <img :src="circleIcon" alt="" class="tw:w-3 tw:h-3" />
        <span>{{ $t('rc_good_opportunity_graph') }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Highcharts from 'highcharts'
import * as Sentry from '@sentry/vue'

import OpportunityBoostChartTooltip from '@/components/rateConnect/opportunityArea/boostOpportunities/OpportunityBoostChartTooltip.vue'

import { useFormatting } from '@/composables/useFormatting'
import { useIsMobile } from '@/composables/useIsMobile'
import {
  findCardForDate,
  DEMAND_VALUE_BY_TYPE,
  getSearchDemandBarColor,
  getSearchDemandBarHoverColor,
} from '@/composables/useBoostOpportunityNavigation'

const props = defineProps({
  chartData: {
    type: Array,
    default: () => [],
  },
  cards: {
    type: Array,
    default: () => [],
  },
})

const { t, locale } = useI18n()
const { formatDate } = useFormatting()
const { isMobile } = useIsMobile()
const containerName = 'opportunity-boost-opportunities-chart'
const chartWrapperRef = ref(null)
const activeTooltip = ref(null)
const pinnedBarIndex = ref(null)

const starIcon = new URL('/src/assets/img/rate-connect/star-icon.svg', import.meta.url).href
const circleIcon = new URL('/src/assets/img/rate-connect/circle-icon.svg', import.meta.url).href

const ICON_STYLE = 'display:block;margin:4px auto 0;'

const STAR_ICON_HTML = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="${ICON_STYLE}"><path fill-rule="evenodd" clip-rule="evenodd" d="M11.9887 5.21C11.9367 4.935 11.7187 4.722 11.4437 4.676L7.83972 4.076L6.62972 0.455C6.54172 0.184 6.28672 0 5.99972 0C5.71272 0 5.45772 0.184 5.36772 0.456L4.15972 4.076L0.556724 4.676C0.281724 4.722 0.0637239 4.936 0.0117239 5.209C-0.0402761 5.482 0.0837239 5.759 0.323724 5.904L3.19972 7.629L2.03472 11.122C1.94172 11.402 2.04472 11.712 2.28472 11.88C2.39972 11.96 2.53472 12 2.66672 12C2.81472 12 2.96172 11.95 3.08272 11.854L5.99972 9.519L8.91672 11.852C9.03672 11.95 9.18672 11.999 9.33272 11.999C9.46572 11.999 9.59972 11.959 9.71272 11.879C9.95672 11.709 10.0587 11.401 9.96472 11.121L8.79972 7.629L11.6757 5.904C11.9167 5.76 12.0407 5.483 11.9887 5.21Z" fill="#D87125"/></svg>`

const CIRCLE_ICON_HTML = `<svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" style="${ICON_STYLE}"><path d="M12 6C12 9.31371 9.31371 12 6 12C2.68629 12 0 9.31371 0 6C0 2.68629 2.68629 0 6 0C9.31371 0 12 2.68629 12 6Z" fill="#D87125"/></svg>`

let chartInstance = null
let hideTooltipTimeout = null

// break labels into multiple lines on mobile
const formatDemandAxisLabel = label => {
  if (!isMobile.value || typeof label !== 'string') return label

  return label.replace(/\s+/g, '<br/>')
}

const getDemandValue = type => {
  return type && DEMAND_VALUE_BY_TYPE[type] ? DEMAND_VALUE_BY_TYPE[type] : null
}

const getOpportunityIconHtml = opportunityType => {
  if (opportunityType === 'HOT_OPPORTUNITY') {
    return STAR_ICON_HTML
  }
  if (opportunityType === 'OPPORTUNITY') {
    return CIRCLE_ICON_HTML
  }
  return `<span style="display:block;height:16px;margin-top:4px;"></span>`
}

const showTooltipForPoint = point => {
  clearTimeout(hideTooltipTimeout)

  const item = props.chartData[point.index]
  if (!item) return

  const chart = point.series.chart
  const card = findCardForDate(props.cards, item.searchedCheckinDate)

  activeTooltip.value = {
    item,
    card,
    position: {
      left: chart.plotLeft + point.plotX,
      top: chart.plotTop + (point.shapeArgs?.y ?? 0),
    },
  }
}

const hideTooltip = () => {
  clearTimeout(hideTooltipTimeout)
  activeTooltip.value = null
  pinnedBarIndex.value = null
}

const pinTooltipForPoint = point => {
  if (pinnedBarIndex.value === point.index) {
    hideTooltip()
    return
  }

  showTooltipForPoint(point)
  pinnedBarIndex.value = point.index
}

const scheduleHideTooltip = () => {
  hideTooltipTimeout = setTimeout(() => {
    if (pinnedBarIndex.value === null) {
      activeTooltip.value = null
    }
  }, 150)
}

const keepTooltipOpen = () => {
  clearTimeout(hideTooltipTimeout)
}

const onChartClickOutside = () => {
  if (pinnedBarIndex.value !== null) {
    hideTooltip()
  }
}

const onChartAreaClick = event => {
  if (pinnedBarIndex.value === null) return

  if (event.target.closest('.highcharts-point')) return
  if (event.target.closest('.boost-opportunity-chart-tooltip')) return

  hideTooltip()
}

const handleEscape = event => {
  if (event.key === 'Escape' && pinnedBarIndex.value !== null) {
    hideTooltip()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleEscape)
})

onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleEscape)
  clearChart()
})

const chartPoints = computed(() => {
  return props.chartData.map(item => {
    const demandValue = getDemandValue(item.demandType)

    return {
      y: demandValue,
      color: getSearchDemandBarColor(item.opportunityType),
      states: {
        hover: {
          color: getSearchDemandBarHoverColor(item.opportunityType),
          brightness: 0,
        },
      },
    }
  })
})

const xAxisCategories = computed(() => {
  return props.chartData.map(item => {
    const currentDate = new Date(item.searchedCheckinDate)
    const day = formatDate(currentDate, { day: 'numeric' })
    const weekday = formatDate(currentDate, { weekday: 'short' })
    const iconHtml = getOpportunityIconHtml(item.opportunityType)

    return `<div style="text-align:center;line-height:14px;"><span>${day}</span><br/><span style="font-size:10px;color:#6C6C6B;">${weekday}</span><br/>${iconHtml}</div>`
  })
})

const clearChart = () => {
  hideTooltip()

  if (chartInstance) {
    chartInstance.destroy()
    chartInstance = null
  }
}

const drawChart = () => {
  if (!chartPoints.value.length) {
    clearChart()
    return
  }

  nextTick(() => {
    try {
      if (!document.getElementById(containerName)) return

      clearChart()

      chartInstance = Highcharts.chart(containerName, {
        chart: {
          type: 'column',
          marginTop: 12,
          marginBottom: 80,
          spacingBottom: 0,
          style: {
            fontFamily: `SourceSansPro, Sans-Serif, "Sans Serif"`,
          },
          events: {
            load() {
              this.container.style.overflow = 'visible'
            },
          },
        },
        title: null,
        credits: {
          enabled: false,
        },
        xAxis: {
          categories: xAxisCategories.value,
          labels: {
            useHTML: true,
            rotation: 0,
            autoRotation: [0],
            y: 20,
          },
          tickLength: 0,
          lineColor: '#D9D8D6',
        },
        yAxis: {
          title: null,
          min: 0,
          max: 5,
          tickPositions: [0, 1, 2, 3, 4, 5],
          gridLineColor: '#D9D8D6',
          gridLineDashStyle: 'Dash',
          labels: {
            useHTML: true,
            formatter: function () {
              const labelsByValue = {
                0: '0',
                1: t('rc_demand_very_low'),
                2: t('rc_demand_low'),
                3: t('rc_demand_medium'),
                4: t('rc_demand_high'),
                5: t('rc_demand_very_high'),
              }

              const label = labelsByValue[this.value] ?? this.value

              return formatDemandAxisLabel(label)
            },
            style: {
              fontSize: '12px',
              color: '#171717',
              lineHeight: '14px',
              textAlign: 'right',
            },
          },
        },
        tooltip: {
          enabled: false,
        },
        legend: {
          enabled: false,
        },
        plotOptions: {
          column: {
            borderRadius: 3,
            pointPadding: 0.08,
            groupPadding: 0.12,
            point: {
              events: {
                click() {
                  pinTooltipForPoint(this)
                },
                mouseOver() {
                  if (pinnedBarIndex.value === null) {
                    showTooltipForPoint(this)
                  }
                },
                mouseOut() {
                  if (pinnedBarIndex.value === null) {
                    scheduleHideTooltip()
                  }
                },
              },
            },
          },
        },
        series: [
          {
            name: t('rc_search_demand'),
            data: chartPoints.value,
          },
        ],
      })
    } catch (error) {
      Sentry.captureException(error)
    }
  })
}

watch(
  () => [props.chartData, props.cards, locale.value, isMobile.value],
  () => {
    drawChart()
  },
  { deep: true, immediate: true }
)
</script>

<style scoped>
.opportunity-boost-opportunities-chart :deep(.highcharts-container),
.opportunity-boost-opportunities-chart :deep(svg) {
  overflow: visible;
}
</style>
